import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { fetchUserById } from "../services/userService";
import { User } from "../types/user.types";
import { CognitoAuthService } from "../../../services/cognitoService";
import { debug } from "../../../utils/debugLogger";

const useCurrentUser = () => {
  const { userId, idToken, userGroups } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => setRefetchTrigger((t) => t + 1);

  useEffect(() => {
    // Initialize loading state - this is crucial
    let isMounted = true;

    const fetchUser = async () => {
      // Only set loading if the component is still mounted
      if (isMounted) setLoading(true);
      if (isMounted) setError(null);
      
      // Check if we even need to fetch
      if (!userId || !idToken) {
        debug.log("No userId or idToken, skipping fetch");
        if (isMounted) {
          setError("Missing user ID or token");
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        debug.log("Fetching user with ID:", userId);
        
        // Get the correct user ID - either from token or from context
        let correctUserId = "";
        // First, try to get user ID from token (most reliable)
        const cognitoId = CognitoAuthService.getUserId(idToken);

        if (cognitoId) {
          correctUserId = cognitoId;
        } 
        // Fallback: Check if userId from context is valid (not an email)
        else if (userId && !userId.includes('@')) {
          correctUserId = userId;
        } else {
          debug.warn("UserId appears to be an email or invalid:", userId);
          if (isMounted) {
            setError("Could not determine valid user ID");
            setUser(null);
            setLoading(false);
          }
          return;
        }
        
        debug.log("Using user ID:", correctUserId);
        
      
        
        debug.log("Fetching user with ID:", correctUserId);
        
        try {
          // Set a timeout to avoid hanging
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Fetch user timeout")), 10000)
          );
          
          // fetchUserById will handle the user_id_ prefix
          const fetchPromise = fetchUserById(correctUserId, idToken);
          
          // Race the fetch against a timeout
          const fetchedUser = await Promise.race([fetchPromise, timeoutPromise]) as User;
          debug.log("Fetch completed successfully:", fetchedUser);
          
          if (isMounted) {
            setUser(fetchedUser);
            setError(null);
          }
        } catch (err: any) {
          debug.log("Error fetching user:", err);
          // For "User not found" errors, we'll still return null but use a special error code
          // This indicates the user exists in Cognito but not in our database
          if (err.message === "User not found") {
            if (isMounted) {
              setError("USER_NOT_IN_DB");
              setUser(null);
            }
          } else {
            // For other errors (server error, network error, etc.)
            if (isMounted) {
              setError(err.message || "Failed to fetch user");
              setUser(null);
            }
          }
        }
      } catch (err: any) {
        debug.error("Unexpected error in useCurrentUser:", err);
        if (isMounted) {
          setError(err.message || "Failed to fetch user");
          setUser(null);
        }
      }
      
      // Always set loading to false at the end if still mounted
      if (isMounted) {
        debug.log("Setting loading to false");
        setLoading(false);
      }
    };
    
    fetchUser();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [userId, idToken, refetchTrigger]);

  return { user, loading, error, idToken, userGroups, refetch };
};

export default useCurrentUser;
